// Cliente mínimo de Amazon Cognito (Req. 17.2, 19.11).
//
// En lugar de arrastrar el SDK completo de Cognito al bundle de la PWA, se usa
// directamente la API JSON de Cognito Identity Provider (`AWSCognitoIdentity
// ProviderService`) vía `fetch`. Cubre lo que necesita el frontend:
//   - `USER_PASSWORD_AUTH` (login) → tokens (id/access/refresh).
//   - `ForgotPassword` / `ConfirmForgotPassword` → recuperación por email, que
//     NO revela si el correo existe (el backend/Cognito responde igual — Req. 19.12).
//   - `NEW_PASSWORD_REQUIRED` (primer login de un usuario invitado).
//
// Requiere que el App client de Cognito tenga habilitado `USER_PASSWORD_AUTH`.

import { config } from '../config.js';

const TARGET_PREFIX = 'AWSCognitoIdentityProviderService';

/** Tokens devueltos por Cognito tras autenticar. */
export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Segundos de validez del token de acceso. */
  expiresIn: number;
}

/** Error de Cognito con el tipo (`__type`) para ramificar la UI. */
export class CognitoError extends Error {
  constructor(
    readonly type: string,
    message: string,
  ) {
    super(message);
    this.name = 'CognitoError';
  }
}

/** Desafío pendiente (p. ej. fijar nueva contraseña en el primer login). */
export interface AuthChallenge {
  challenge: 'NEW_PASSWORD_REQUIRED';
  session: string;
  username: string;
}

/** Resultado de un intento de login: tokens o un desafío pendiente. */
export type LoginOutcome = { tokens: CognitoTokens } | { challenge: AuthChallenge };

function endpoint(): string {
  return `https://cognito-idp.${config.cognitoRegion}.amazonaws.com/`;
}

/** Ejecuta una acción de la API JSON de Cognito. */
async function call<T>(action: string, body: unknown): Promise<T> {
  const res = await fetch(endpoint(), {
    method: 'POST',
    headers: {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': `${TARGET_PREFIX}.${action}`,
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!res.ok) {
    const type = String(json['__type'] ?? 'UnknownError');
    const message = String(json['message'] ?? `Cognito ${action} falló (${res.status}).`);
    throw new CognitoError(type, message);
  }
  return json as T;
}

interface AuthResultJson {
  AuthenticationResult?: {
    IdToken?: string;
    AccessToken?: string;
    RefreshToken?: string;
    ExpiresIn?: number;
  };
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
}

function toTokens(result: AuthResultJson['AuthenticationResult']): CognitoTokens {
  return {
    idToken: result?.IdToken ?? '',
    accessToken: result?.AccessToken ?? '',
    refreshToken: result?.RefreshToken,
    expiresIn: result?.ExpiresIn ?? 3600,
  };
}

/** Resultado del alta pública (auto-registro). */
export interface SignUpResult {
  /** ¿Requiere confirmar el correo con un código? (según la config del pool). */
  needsConfirmation: boolean;
}

/**
 * Alta pública de un usuario (auto-registro) contra Cognito (`SignUp`).
 *
 * IMPORTANTE para la validación de correo duplicado: a diferencia del login o
 * del "olvidé mi contraseña" (donde Cognito oculta la existencia del usuario si
 * `PreventUserExistenceErrors` está activo), la operación `SignUp` SÍ devuelve
 * `UsernameExistsException` cuando el correo ya está registrado. Esa excepción
 * es la que la UI usa para bloquear el alta con un correo ya existente.
 *
 * Si el User Pool está en modo "solo el administrador crea usuarios"
 * (AllowAdminCreateUserOnly = true), Cognito responde `NotAuthorizedException`:
 * el auto-registro público no está habilitado y la UI lo informa.
 */
export async function signUp(
  email: string,
  password: string,
  name?: string,
  locale?: string,
): Promise<SignUpResult> {
  const attrs: Array<{ Name: string; Value: string }> = [{ Name: 'email', Value: email }];
  if (name && name.trim() !== '') attrs.push({ Name: 'name', Value: name.trim() });
  // i18n: idioma elegido en el registro → atributo estándar `locale` de Cognito,
  // que el trigger CustomMessage usa para enviar el correo de verificación en el
  // idioma del usuario.
  if (locale && locale.trim() !== '') attrs.push({ Name: 'locale', Value: locale.trim() });
  const json = await call<{ UserConfirmed?: boolean }>('SignUp', {
    ClientId: config.cognitoClientId,
    Username: email,
    Password: password,
    UserAttributes: attrs,
  });
  return { needsConfirmation: json.UserConfirmed === false };
}

/** Confirma el alta con el código de verificación enviado por correo. */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  await call('ConfirmSignUp', {
    ClientId: config.cognitoClientId,
    Username: email,
    ConfirmationCode: code,
  });
}

/** Reenvía el código de verificación del alta. */
export async function resendSignUpCode(email: string): Promise<void> {
  await call('ResendConfirmationCode', {
    ClientId: config.cognitoClientId,
    Username: email,
  });
}

/** Login con usuario/contraseña (Req. 17.2). */
export async function login(username: string, password: string): Promise<LoginOutcome> {
  const json = await call<AuthResultJson>('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: config.cognitoClientId,
    AuthParameters: { USERNAME: username, PASSWORD: password },
  });
  if (json.ChallengeName === 'NEW_PASSWORD_REQUIRED' && json.Session) {
    return {
      challenge: {
        challenge: 'NEW_PASSWORD_REQUIRED',
        session: json.Session,
        username: json.ChallengeParameters?.USER_ID_FOR_SRP ?? username,
      },
    };
  }
  return { tokens: toTokens(json.AuthenticationResult) };
}

/** Responde al desafío de nueva contraseña (primer login de un invitado). */
export async function completeNewPassword(
  challenge: AuthChallenge,
  newPassword: string,
): Promise<CognitoTokens> {
  const json = await call<AuthResultJson>('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: config.cognitoClientId,
    Session: challenge.session,
    ChallengeResponses: { USERNAME: challenge.username, NEW_PASSWORD: newPassword },
  });
  return toTokens(json.AuthenticationResult);
}

/** Refresca el token de acceso con el refresh token. */
export async function refresh(refreshToken: string): Promise<CognitoTokens> {
  const json = await call<AuthResultJson>('InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: config.cognitoClientId,
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  });
  const tokens = toTokens(json.AuthenticationResult);
  // El refresh no devuelve un nuevo refresh token; se conserva el anterior.
  return { ...tokens, refreshToken };
}

/**
 * Inicia la recuperación de contraseña (Req. 19.11). Cognito envía un código al
 * email. NO revela si el correo existe (Req. 19.12): siempre responde ok salvo
 * error de configuración.
 */
export async function forgotPassword(username: string): Promise<void> {
  await call('ForgotPassword', {
    ClientId: config.cognitoClientId,
    Username: username,
  });
}

/** Confirma la nueva contraseña con el código recibido por email (Req. 19.11). */
export async function confirmForgotPassword(
  username: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await call('ConfirmForgotPassword', {
    ClientId: config.cognitoClientId,
    Username: username,
    ConfirmationCode: code,
    Password: newPassword,
  });
}

/**
 * Cambia la contraseña de un usuario ya autenticado (Req. 19.13). Usa el
 * `accessToken` de la sesión; la contraseña nunca pasa por nuestro backend.
 * Cognito valida la contraseña anterior y aplica su política de robustez (si la
 * nueva no cumple, responde `InvalidPasswordException`; si la anterior es
 * incorrecta, `NotAuthorizedException`).
 */
export async function changePassword(
  accessToken: string,
  previousPassword: string,
  proposedPassword: string,
): Promise<void> {
  await call('ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}

/** Cierra sesión globalmente (revoca los tokens del access token dado). */
export async function globalSignOut(accessToken: string): Promise<void> {
  try {
    await call('GlobalSignOut', { AccessToken: accessToken });
  } catch {
    // Si falla (token ya expirado), el signout local igualmente procede.
  }
}
